import React from "react";
import { Box, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import StarIcon from "@mui/icons-material/Star";
import CheckIcon from "@mui/icons-material/Check";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { useCustomTheme } from "../../utils/useCustomTheme";
import moment from "moment";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  formatPlanPriceInDisplay,
  resolvePlanDisplayCurrency,
} from "../../utils/planDisplayCurrency";
import { isPlanFree, isPlanPopular, planNum } from "../../utils/planFields";

const buildFeatures = (plan, lang) => [
  `${planNum(plan, "credits", "credits", 0)} ${lang?.pricing_aiCredits || "AI Credits"}`,
  `${planNum(plan, "max_characters", "maxCharacters", 0)} ${lang?.pricing_aiModels || "AI Characters"}`,
  lang?.creditsNeverExpire || "Unused credits never expire",
];

// ─────────────────────────────────────────────
const PricingCard = ({
  plan,
  history,
  displayCurrency,
  onSelectPlan,
  selectionMode = false,
  selected = false,
}) => {
  const { lang } = React.useContext(TranslateContext);
  const { config, isDark } = useCustomTheme();

  const bgDefault = isDark
    ? config.background_default_dark
    : config.background_default_light;
  const bgPaper = isDark
    ? config.background_paper_dark
    : config.background_paper_light;
  const textPrimary = isDark
    ? config.text_primary_dark
    : config.text_primary_light;
  const textSecondary = isDark
    ? config.text_secondary_dark
    : config.text_secondary_light;
  const borderColor = isDark ? config.border_dark : config.border_light;
  const accentColor = isDark
    ? config.text_accent_dark
    : config.text_accent_light;
  const btnBg = isDark
    ? config.button.contained.backgroundColor_dark
    : config.button.contained.backgroundColor_light;
  const btnColor = isDark
    ? config.button.contained.color_dark
    : config.button.contained.color_light;

  const isPopular = isPlanPopular(plan);
  const isFree = isPlanFree(plan);
  const features = buildFeatures(plan, lang);
  const usdPrice = planNum(plan, "price", "price", 0);
  const usdStrike = planNum(plan, "price_strike", "priceStrike", 0);
  const sym = displayCurrency?.symbol || "₦";
  const code = displayCurrency?.code || "NGN";
  const localPrice = formatPlanPriceInDisplay(usdPrice, displayCurrency);
  const localStrike = formatPlanPriceInDisplay(usdStrike, displayCurrency);
  const createdRaw = plan.createdAt ?? plan.created_at;
  const createdLabel = createdRaw && moment(createdRaw).isValid()
    ? moment(createdRaw).format("MMM YYYY")
    : "—";
  const choosePlan = (event) => {
    event?.preventDefault?.();
    const publicPlanId = plan.public_id || plan.publicId;
    if (!publicPlanId) return;
    const checkoutPath = `/checkout/${publicPlanId}`;
    // Prefer explicit selection callbacks (e.g. signup stays on page), otherwise go to checkout.
    if (typeof onSelectPlan === "function") {
      onSelectPlan({ plan, checkoutPath });
      return;
    }
    history.push(checkoutPath);
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: `${config.card.borderRadius}px`,
        border: `1.5px solid ${
          selected || isPopular ? accentColor || btnBg : borderColor
        }`,
        outline: selected ? `2px solid ${accentColor || btnBg}` : "none",
        outlineOffset: 2,
        background: isPopular
          ? isDark
            ? `linear-gradient(145deg, ${bgPaper} 0%, ${bgDefault} 100%)`
            : `linear-gradient(145deg, #fff 0%, #f8f8ff 100%)`
          : bgPaper,
        p: { xs: 3, md: 3.5 },
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: isPopular
          ? isDark
            ? `0 0 0 1px ${accentColor || btnBg}33, 0 24px 60px rgba(0,0,0,0.55)`
            : `0 0 0 1px ${accentColor || btnBg}22, 0 24px 60px rgba(0,0,0,0.12)`
          : isDark
            ? "0 8px 32px rgba(0,0,0,0.4)"
            : "0 8px 32px rgba(0,0,0,0.07)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: isDark
            ? "0 32px 80px rgba(0,0,0,0.7)"
            : "0 32px 80px rgba(0,0,0,0.15)",
        },
        width: { xs: "100%", md: isPopular ? 310 : 280 },
        flexShrink: 0,
      }}
    >
      {/* ── Popular badge ── */}
      {isPopular && (
        <Box
          sx={{
            position: "absolute",
            top: -13,
            left: "50%",
            transform: "translateX(-50%)",
            background: accentColor || btnBg,
            borderRadius: "50px",
            px: 2,
            py: 0.4,
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <StarIcon sx={{ fontSize: "0.65rem", color: btnColor }} />
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              color: btnColor,
              fontFamily: config.font_family,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {lang?.pricing_mostPopular || "Most Popular"}
          </Typography>
        </Box>
      )}

      {/* ── Title + since date ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.05rem",
            fontWeight: config.font_weight_bold,
            color: textPrimary,
            fontFamily: config.font_family,
          }}
        >
          {plan.title}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: textSecondary,
            fontFamily: config.font_family,
            opacity: 0.5,
          }}
        >
          {lang?.pricing_since || "Since"} {createdLabel}
        </Typography>
      </Box>

      {/* ── Price ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.8,
          mt: 2,
          mb: 0.5,
        }}
      >
        {isFree ? (
          <Typography
            sx={{
              fontSize: "2.4rem",
              fontWeight: config.font_weight_bold,
              color: textPrimary,
              fontFamily: config.font_family,
              lineHeight: 1,
            }}
          >
            {lang?.pricing_free || "Free"}
          </Typography>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: "2.4rem",
                fontWeight: config.font_weight_bold,
                color: textPrimary,
                fontFamily: config.font_family,
                lineHeight: 1,
              }}
            >
              {sym}
              {localPrice}
            </Typography>
            {/* Period label ("/ 1 month") hidden — plan length is not shown on pricing cards. */}
          </>
        )}
      </Box>

      {/* ── Strike price ── */}
      {usdStrike > 0 && (
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: textSecondary,
            fontFamily: config.font_family,
            textDecoration: "line-through",
            mb: 2.5,
            opacity: 0.55,
          }}
        >
          {lang?.pricing_was || "Was"} {sym}
          {localStrike}
        </Typography>
      )}

      {/* ── Pills: Credits + Expiry + Max Models ── */}
      <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 3, flexWrap: "wrap" }}>
        {/* Credits pill */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.2,
            py: 0.45,
            borderRadius: "50px",
            border: `1px solid ${accentColor || btnBg}55`,
            background: isDark
              ? `${accentColor || btnBg}15`
              : `${accentColor || btnBg}10`,
          }}
        >
          <BoltIcon sx={{ fontSize: "0.8rem", color: accentColor || btnBg }} />
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: config.font_weight_semibold,
              color: accentColor || btnBg,
              fontFamily: config.font_family,
            }}
          >
            {planNum(plan, "credits", "credits", 0)} {lang?.pricing_credits || "Credits"}
          </Typography>
        </Box>

        {/* Expiry pill (calendar + "1 month") — hidden from pricing cards.
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.2,
            py: 0.45,
            borderRadius: "50px",
            border: `1px solid ${borderColor}`,
            background: isDark ? config.glass_bg_dark : config.glass_bg_light,
          }}
        >
          <CalendarTodayIcon
            sx={{ fontSize: "0.8rem", color: textSecondary }}
          />
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: config.font_weight_medium,
              color: textSecondary,
              fontFamily: config.font_family,
            }}
          >
            {expiryLabel}
          </Typography>
        </Box>
        */}

        {/* Max Models pill */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1.2,
            py: 0.45,
            borderRadius: "50px",
            border: `1px solid ${borderColor}`,
            background: isDark ? config.glass_bg_dark : config.glass_bg_light,
          }}
        >
          <SmartToyIcon sx={{ fontSize: "0.8rem", color: textSecondary }} />
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: config.font_weight_medium,
              color: textSecondary,
              fontFamily: config.font_family,
            }}
          >
            {planNum(plan, "max_characters", "maxCharacters", 0)} {lang?.pricing_models || "Models"}
          </Typography>
        </Box>
      </Box>

      {/* ── Divider ── */}
      <Box
        sx={{ height: "1px", background: borderColor, mb: 2.5, opacity: 0.5 }}
      />

      {/* ── Features list ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.2,
          flex: 1,
          mb: 3,
        }}
      >
        {features.map((feat) => (
          <Box
            key={feat}
            sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
          >
            <Box
              sx={{
                mt: 0.15,
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: isDark
                  ? `${accentColor || btnBg}22`
                  : `${accentColor || btnBg}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckIcon
                sx={{ fontSize: "0.7rem", color: accentColor || btnBg }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: textSecondary,
                fontFamily: config.font_family,
                lineHeight: 1.5,
              }}
            >
              {feat}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── CTA ── */}
      <Box
        onClick={choosePlan}
        component="a"
        sx={{
          width: "100%",
          py: 1.3,
          borderRadius: "50px",
          background: isPopular ? accentColor || btnBg : "transparent",
          border: `1.5px solid ${isPopular ? "transparent" : borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          textDecoration: "none",
          transition: "all 0.25s ease",
          "&:hover": {
            background: accentColor || btnBg,
            borderColor: "transparent",
            transform: "scale(1.02)",
          },
          "&:hover .cta-text": { color: btnColor },
        }}
      >
        <Typography
          className="cta-text"
          sx={{
            fontSize: "0.85rem",
            fontWeight: config.font_weight_semibold,
            color: isPopular ? btnColor : textPrimary,
            fontFamily: config.font_family,
            transition: "color 0.25s ease",
          }}
        >
          {selectionMode && selected
            ? lang?.pricing_selected || "Selected"
            : lang?.getStarted || "Get Started"}{" "}
          →
        </Typography>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────
const Pricing = ({
  showHeader = true,
  onSelectPlan,
  selectionMode = false,
  selectedPublicId = "",
}) => {
  const history = useHistory();
  const [plans, setPlans] = React.useState([]);
  const [displayCurrency, setDisplayCurrency] = React.useState({
    symbol: "₦",
    code: "NGN",
    rate: 1550,
  });
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const { config, isDark } = useCustomTheme();

  const bgDefault = isDark
    ? config.background_default_dark
    : config.background_default_light;
  const textPrimary = isDark
    ? config.text_primary_dark
    : config.text_primary_light;
  const textSecondary = isDark
    ? config.text_secondary_dark
    : config.text_secondary_light;
  const borderColor = isDark ? config.border_dark : config.border_light;
  const accentColor = isDark
    ? config.text_accent_dark
    : config.text_accent_light;
  const btnBg = isDark
    ? config.button.contained.backgroundColor_dark
    : config.button.contained.backgroundColor_light;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const [planRes, gwRes] = await Promise.all([
        hitAxios({
          path: "/api/plan/get_all",
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        }),
        hitAxios({
          path: "/api/payment/active-gateways",
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        }),
      ]);
      if (cancelled) return;
      if (gwRes?.data?.success) {
        const { currency } = resolvePlanDisplayCurrency(gwRes.data.data);
        setDisplayCurrency(currency);
      }
      if (planRes?.data?.success) {
        const raw = Array.isArray(planRes.data.plans)
          ? planRes.data.plans
          : planRes.data.data || [];
        const sorted = raw.filter((plan) => planNum(plan, "price", "price", 0) > 0).sort(
          (a, b) => planNum(a, "price", "price", 0) - planNum(b, "price", "price", 0),
        );
        setPlans(sorted);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hitAxios]);

  return (
    <Box
      sx={{
        background: bgDefault,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: showHeader ? { xs: 3, md: 8, lg: 12 } : { xs: 1, md: 2 },
        py: showHeader ? { xs: 4, md: 5 } : { xs: 2, md: 3 },
        overflow: "hidden",
      }}
    >
      {/* ── Header ── */}
      {showHeader && <Box sx={{ textAlign: "center", mb: 3.5, maxWidth: 620 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            border: `1px solid ${borderColor}`,
            borderRadius: "50px",
            px: 1.5,
            py: 0.5,
            mb: 2.5,
          }}
        >
          <FiberManualRecordIcon
            sx={{
              fontSize: "0.45rem",
              color: accentColor || btnBg,
              filter: `drop-shadow(0 0 4px ${accentColor || btnBg})`,
              animation: "pulse 2s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.4 },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: config.font_weight_semibold,
              color: textSecondary,
              fontFamily: config.font_family,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {lang?.pricing_badge || "Simple Pricing"}
          </Typography>
        </Box>

        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "2.8rem" },
            fontWeight: config.font_weight_bold,
            color: textPrimary,
            fontFamily: config.font_family,
            lineHeight: 1.15,
            letterSpacing: "-1px",
            mb: 1.5,
          }}
        >
          {lang?.pricing_headline1 || "One plan for every"}{" "}
          <Box component="span" sx={{ color: accentColor || textSecondary }}>
            {lang?.pricing_headlineAccent || "creator."}
          </Box>
        </Typography>

        <Typography
          sx={{
            fontSize: "1rem",
            color: textSecondary,
            fontFamily: config.font_family,
            lineHeight: 1.7,
          }}
        >
          {lang?.pricing_subheadline ||
            "Choose the credit package that fits your workflow. Pay securely and use your credits across the entire AI studio."}
        </Typography>
      </Box>}

      {/* ── Cards ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          justifyContent: "center",
          gap: { xs: 3, md: 2.5 },
          width: "100%",
          maxWidth: "960px",
        }}
      >
        {plans.map((plan) => (
          <PricingCard
            history={history}
            key={plan.id}
            plan={plan}
            displayCurrency={displayCurrency}
            onSelectPlan={onSelectPlan}
            selectionMode={selectionMode}
            selected={
              String(selectedPublicId || "") ===
              String(plan.public_id || plan.publicId || "")
            }
          />
        ))}
      </Box>

      {showHeader && (
        <Typography
          sx={{
            mt: 3,
            fontSize: "0.78rem",
            color: textSecondary,
            fontFamily: config.font_family,
            opacity: 0.6,
            textAlign: "center",
          }}
        >
          {lang?.pricing_footerNote ||
            "All plans include access to the AI studio. Purchased credits remain available until you use them."}
        </Typography>
      )}
    </Box>
  );
};

export default Pricing;
